/**
 * Call apiKeyStore.init() from +layout.svelte onMount to validate any stored key on startup.
 */

import * as Sentry from '@sentry/sveltekit';
import { geoServer } from '#lib/config.js';
import { analyticsStore } from '#lib/stores/analyticsStore.js';
import { FetchStatus } from '#lib/types.js';

// Mirrors ApiError enum from safesystem_apiKey-manager — user-facing messages
enum ApiError {
	APIKEY_INVALID = "This key isn't registered, please try again.",
	NETWORK_ERROR = "We couldn't connect to the server, please check your internet connection or try again later."
}

// Must exactly match safesystem_apiKey-manager — key is shared across apps on the same domain
const STORAGE_KEY = 'platform:apiKey';
export const API_KEY_LENGTH = 40;

function createApiKeyStore() {
	const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;

	let apiKey = $state<string | null>(null);
	// Client namespace from validateApikey(); every later GeoServer call needs it.
	let namespace = $state<string | null>(null);
	// Start LOADING if a stored key exists (init() will validate it); IDLE if no key
	let status = $state<FetchStatus>(stored ? FetchStatus.LOADING : FetchStatus.IDLE);
	let error = $state<ApiError | null>(null);
	// True when init() rejects a stored key — lets layout.svelte unblock the gate
	// even when the health-check also returned CONNECTION_ERROR.
	let startupKeyRejected = $state(false);

	// The client namespace for a valid key, else undefined.
	const validate = async (key: string) =>
		key.length === API_KEY_LENGTH ? await geoServer.validateApikey(key) : undefined;

	async function accept(key: string, ns: string) {
		apiKey = key;
		namespace = ns;
		status = FetchStatus.SUCCESS;
		const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(key));
		const id = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
		Sentry.setUser({ id });
		analyticsStore.logApiAccess(id);
	}

	return {
		get apiKey() {
			return apiKey;
		},
		// Both, or null until a key is validated.
		get credentials(): { apiKey: string; namespace: string } | null {
			return apiKey && namespace ? { apiKey, namespace } : null;
		},
		get apiState() {
			return { status, error };
		},
		get startupKeyRejected() {
			return startupKeyRejected;
		},

		/**
		 * Validate any stored key on app startup. Call from +layout.svelte onMount.
		 */
		async init() {
			const key = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
			if (!key) {
				status = FetchStatus.IDLE;
				return;
			}
			try {
				const ns = await validate(key);
				if (ns) {
					await accept(key, ns);
				} else {
					localStorage.removeItem(STORAGE_KEY);
					status = FetchStatus.IDLE;
					startupKeyRejected = true;
				}
			} catch {
				// Network error on startup — keep the stored key, let user retry
				status = FetchStatus.FAILURE;
				error = ApiError.NETWORK_ERROR;
			}
		},

		/** Validate the supplied key against GeoServer, then persist to localStorage. */
		async validateAndSave(key: string) {
			status = FetchStatus.LOADING;
			error = null;
			try {
				const ns = await validate(key);
				if (ns) {
					localStorage.setItem(STORAGE_KEY, key);
					await accept(key, ns);
				} else {
					localStorage.removeItem(STORAGE_KEY);
					apiKey = null;
					namespace = null;
					status = FetchStatus.IDLE;
					error = ApiError.APIKEY_INVALID;
				}
			} catch {
				// Network error — do NOT clear the stored key (user may just be temporarily offline)
				status = FetchStatus.FAILURE;
				error = ApiError.NETWORK_ERROR;
			}
		},

		/** Clear the stored API key and reset state. */
		clear() {
			if (typeof localStorage !== 'undefined') localStorage.removeItem(STORAGE_KEY);
			apiKey = null;
			namespace = null;
			status = FetchStatus.IDLE;
			error = null;
			Sentry.setUser(null);
		}
	};
}

export const apiKeyStore = createApiKeyStore();
